import React from "react";
import { Img, interpolateColors, staticFile } from "remotion";
import { useCopy } from "../copy";
import { BRANDS, type Brand, STORY_BRAND } from "../data/brands";
import { LOOK } from "../look";

/**
 * Coded replicas of the Frak app screens, fed by the live brand snapshot
 * (`data/brands.generated.ts`), so the video never shows a brand or a
 * reward that is no longer live, and the EN cut gets EN labels.
 *
 * Authored in iOS points on a 393pt-wide screen (iPhone 15), then scaled
 * to the device's screen width with `AppViewport`.
 */
export const PT_WIDTH = 393;

const APP_BG = LOOK.bg2;

/** Scales a 393pt-wide screen to `screenWidth` px. */
export const AppViewport: React.FC<{
  screenWidth: number;
  children: React.ReactNode;
}> = ({ screenWidth, children }) => (
  <div
    style={{
      position: "absolute",
      left: 0,
      top: 0,
      width: PT_WIDTH,
      height: 2000,
      scale: String(screenWidth / PT_WIDTH),
      transformOrigin: "0 0",
      fontFamily: LOOK.body,
      color: LOOK.ink,
    }}
  >
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Chrome
// ---------------------------------------------------------------------------

const StatusBar: React.FC = () => (
  <div
    style={{
      position: "absolute",
      left: 0,
      top: 0,
      width: PT_WIDTH,
      height: 54,
      background: APP_BG,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "6px 30px 0 44px",
      zIndex: 2,
    }}
  >
    <span style={{ fontSize: 17, fontWeight: LOOK.semi, letterSpacing: -0.3 }}>9:41</span>
    <svg width="72" height="13" viewBox="0 0 72 13" fill={LOOK.ink}>
      {/* signal */}
      <rect x="0" y="8" width="3" height="4" rx="1" />
      <rect x="5" y="6" width="3" height="6" rx="1" />
      <rect x="10" y="3" width="3" height="9" rx="1" />
      <rect x="15" y="0" width="3" height="12" rx="1" />
      {/* wifi */}
      <path d="M31 12l-2.6-3a3.7 3.7 0 015.2 0L31 12zm-5-5.6a7.2 7.2 0 0110 0l-1.3 1.5a5.3 5.3 0 00-7.4 0L26 6.4zm-2.7-3a11 11 0 0115.4 0l-1.3 1.5a9 9 0 00-12.8 0L23.3 3.4z" />
      {/* battery */}
      <rect x="45" y="0.5" width="23" height="12" rx="3.5" fill="none" stroke={LOOK.ink} strokeOpacity="0.4" />
      <rect x="47" y="2.5" width="19" height="8" rx="2" />
      <rect x="69.5" y="4.5" width="1.6" height="4" rx="0.8" fillOpacity="0.4" />
    </svg>
  </div>
);

type Tab = 0 | 1 | 2;

const TabIcon: React.FC<{ tab: Tab; color: string }> = ({ tab, color }) => {
  if (tab === 0)
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
        <rect x="3" y="5" width="18" height="15" rx="3" />
        <path d="M3 9h18M16 14h2" strokeLinecap="round" />
      </svg>
    );
  if (tab === 1)
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
        <rect x="3" y="3" width="8" height="8" rx="2.5" />
        <rect x="13" y="3" width="8" height="8" rx="2.5" />
        <rect x="3" y="13" width="8" height="8" rx="2.5" />
        <rect x="13" y="13" width="8" height="8" rx="2.5" />
      </svg>
    );
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
      <circle cx="12" cy="8" r="4.5" />
      <path d="M3.5 21a8.5 8 0 0117 0z" />
    </svg>
  );
};

/** Floating tab bar, pinned to the bottom of the (852pt) screen. */
const TabBar: React.FC<{ active: Tab }> = ({ active }) => {
  const copy = useCopy();
  return (
    <div
      style={{
        position: "absolute",
        left: 60,
        right: 60,
        top: 852 - 34 - 64,
        height: 64,
        borderRadius: 32,
        background: "rgba(255,255,255,0.94)",
        boxShadow: "0 6px 24px rgba(0,0,0,0.10)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        padding: "0 6px",
        zIndex: 2,
      }}
    >
      {copy.app.tabs.map((label, i) => {
        const on = i === active;
        const color = on ? LOOK.blue : LOOK.inkSoft;
        return (
          <div
            key={label}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              padding: "6px 12px",
              borderRadius: 26,
              background: on ? LOOK.sky : "transparent",
              fontSize: 10,
              fontWeight: LOOK.semi,
              color,
            }}
          >
            <TabIcon tab={i as Tab} color={color} />
            {label}
          </div>
        );
      })}
    </div>
  );
};

const LargeTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      position: "absolute",
      left: 16,
      top: 62,
      fontFamily: LOOK.body,
      fontWeight: LOOK.bold,
      fontSize: 32,
      letterSpacing: -0.6,
    }}
  >
    {children}
  </div>
);

const BrandLogo: React.FC<{ brand: Brand; size: number; style?: React.CSSProperties }> = ({
  brand,
  size,
  style,
}) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: size,
      overflow: "hidden",
      background: LOOK.white,
      border: `1px solid ${LOOK.hairline}`,
      flex: "none",
      ...style,
    }}
  >
    {brand.logo ? (
      <Img src={staticFile(brand.logo)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    ) : null}
  </div>
);

// ---------------------------------------------------------------------------
// Explorer
// ---------------------------------------------------------------------------

export const EXPLORER = {
  listTop: 110,
  cardHeight: 232,
  gap: 16,
  imageHeight: 152,
} as const;

/** Top of card `index` in unscrolled points. */
export const explorerCardTop = (index: number) =>
  EXPLORER.listTop + index * (EXPLORER.cardHeight + EXPLORER.gap);

export const STORY_INDEX = Math.max(0, BRANDS.indexOf(STORY_BRAND));

const GiftIcon: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={LOOK.ink} strokeWidth="2.4">
    <rect x="3" y="8" width="18" height="5" rx="1" />
    <path d="M5 13v8h14v-8M12 8v13M12 8S10.5 3 8 3.5 7.5 8 12 8zm0 0s1.5-5 4-4.5S16.5 8 12 8z" strokeLinejoin="round" />
  </svg>
);

export const ExplorerScreen: React.FC<{
  scrollY: number;
  /** 0..1 selection ring on the story brand's card. */
  selected?: number;
}> = ({ scrollY, selected = 0 }) => {
  const copy = useCopy();
  return (
    <div style={{ position: "absolute", inset: 0, width: PT_WIDTH, height: 2000, background: APP_BG }}>
      <div style={{ position: "absolute", inset: 0, translate: `0 ${-scrollY}px` }}>
        <LargeTitle>{copy.app.explorer}</LargeTitle>
        {BRANDS.map((brand, i) => {
          const isStory = i === STORY_INDEX;
          return (
            <div
              key={brand.id}
              style={{
                position: "absolute",
                left: 16,
                width: PT_WIDTH - 32,
                top: explorerCardTop(i),
                height: EXPLORER.cardHeight,
                borderRadius: 22,
                background: LOOK.white,
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                outline:
                  isStory && selected > 0
                    ? `${3 * selected}px solid ${interpolateColors(selected, [0, 1], ["rgba(0,67,239,0)", LOOK.blue])}`
                    : undefined,
                outlineOffset: -1,
              }}
            >
              <Img
                src={staticFile(brand.hero as string)}
                style={{
                  width: "100%",
                  height: EXPLORER.imageHeight,
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {/* Reward pill, as in the app */}
              <div
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 10px",
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.92)",
                  fontSize: 12,
                  fontWeight: LOOK.semi,
                }}
              >
                <GiftIcon />
                {copy.upTo(brand.reward)}
              </div>
              <BrandLogo
                brand={brand}
                size={44}
                style={{ position: "absolute", left: 12, top: EXPLORER.imageHeight - 26, border: `3px solid ${LOOK.white}` }}
              />
              <div style={{ position: "absolute", left: 16, right: 16, top: EXPLORER.imageHeight + 24 }}>
                <div style={{ fontSize: 18, fontWeight: LOOK.bold, letterSpacing: -0.3 }}>{brand.label}</div>
                <div style={{ fontSize: 14, color: LOOK.inkSoft, marginTop: 2 }}>{copy.app.brandLine}</div>
              </div>
              {isStory && selected > 0 ? (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: LOOK.blue,
                    opacity: 0.08 * selected,
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
      <StatusBar />
      <TabBar active={1} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Wallet
// ---------------------------------------------------------------------------

/** Older history rows: live brands other than the story's, capped rewards. */
const HISTORY = BRANDS.filter((b) => b !== STORY_BRAND)
  .slice(0, 4)
  .map((brand, i) => ({ brand, amount: Math.min(brand.reward, [3, 4, 2, 5][i]) }));

const ROW_H = 68;

const HistoryRow: React.FC<{
  brand: Brand;
  date: string;
  amount: string;
  highlight?: number;
}> = ({ brand, date, amount, highlight = 0 }) => (
  <div
    style={{
      height: ROW_H,
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "0 14px",
      background: interpolateColors(highlight, [0, 1], ["rgba(9,190,103,0)", "rgba(9,190,103,0.12)"]),
    }}
  >
    <BrandLogo brand={brand} size={40} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 15, fontWeight: LOOK.semi, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {brand.label}
      </div>
      <div style={{ fontSize: 12, color: LOOK.inkSoft, marginTop: 2 }}>{date}</div>
    </div>
    <div style={{ fontSize: 14, fontWeight: LOOK.semi, color: LOOK.green }}>{amount}</div>
  </div>
);

export const WalletScreen: React.FC<{
  balance: number;
  /** 0..1 — balance highlight (green + slight scale) while it rolls. */
  flash?: number;
  /** 0..1 — the new purchase row sliding into the history. */
  newRow?: number;
  gain: number;
}> = ({ balance, flash = 0, newRow = 0, gain }) => {
  const copy = useCopy();
  const [whole, cents] = copy.money(balance).replace(/\s*€|€/g, "").trim().split(/[.,]/);
  const sep = copy.money(1.5).includes(",") ? "," : ".";
  return (
    <div style={{ position: "absolute", inset: 0, width: PT_WIDTH, height: 2000, background: APP_BG }}>
      <StatusBar />
      <LargeTitle>{copy.app.wallet}</LargeTitle>

      {/* Balance card */}
      <div
        style={{
          position: "absolute",
          left: 16,
          right: 16,
          top: 112,
          height: 190,
          borderRadius: 22,
          background: LOOK.white,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 20,
        }}
      >
        <div style={{ fontSize: 14, color: LOOK.inkSoft, display: "flex", gap: 6, alignItems: "center" }}>
          {copy.app.totalBalance}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={LOOK.inkSoft} strokeWidth="2">
            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            marginTop: 4,
            scale: String(1 + flash * 0.08),
            color: interpolateColors(flash, [0, 1], [LOOK.ink, LOOK.green]),
          }}
        >
          <span style={{ fontSize: 44, fontWeight: LOOK.semi, letterSpacing: -1, fontVariantNumeric: "tabular-nums" }}>
            {whole}
            {sep}
            {cents}
          </span>
          <span style={{ fontSize: 16, color: LOOK.inkSoft }}>EUR</span>
        </div>
        <div
          style={{
            position: "absolute",
            left: 16,
            right: 16,
            bottom: 18,
            height: 48,
            borderRadius: 48,
            background: LOOK.blue,
            color: LOOK.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: 15,
            fontWeight: LOOK.semi,
          }}
        >
          <span style={{ fontSize: 17 }}>⇄</span>
          {copy.app.transfer}
        </div>
      </div>

      {/* Pending / all-time tiles */}
      {[
        { value: copy.money(8), label: `⏳ ${copy.app.pending}`, color: LOOK.inkSoft },
        { value: copy.money(balance + 20), label: `📈 ${copy.app.allTime}`, color: LOOK.green },
      ].map((tile, i) => (
        <div
          key={tile.label}
          style={{
            position: "absolute",
            top: 314,
            left: 16 + i * ((PT_WIDTH - 32 + 12) / 2),
            width: (PT_WIDTH - 32 - 12) / 2,
            height: 72,
            borderRadius: 18,
            background: LOOK.white,
            padding: "12px 14px",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: LOOK.semi, color: tile.color }}>{tile.value}</div>
          <div style={{ fontSize: 12, color: LOOK.ink, marginTop: 6 }}>{tile.label}</div>
        </div>
      ))}

      {/* History */}
      <div
        style={{
          position: "absolute",
          top: 404,
          left: 16,
          right: 16,
          display: "flex",
          justifyContent: "space-between",
          fontSize: 15,
        }}
      >
        <span style={{ fontWeight: LOOK.semi }}>{copy.app.history}</span>
        <span style={{ color: LOOK.blue, fontWeight: LOOK.medium }}>{copy.app.seeAll}</span>
      </div>
      <div
        style={{
          position: "absolute",
          top: 436,
          left: 16,
          right: 16,
          borderRadius: 22,
          background: LOOK.white,
          overflow: "hidden",
        }}
      >
        {/* New purchase: grows in from zero height at the top of the list. */}
        <div style={{ height: ROW_H * newRow, overflow: "hidden" }}>
          <HistoryRow
            brand={STORY_BRAND}
            date={copy.app.justNow}
            amount={`+${copy.money(gain)}`}
            highlight={newRow}
          />
        </div>
        {HISTORY.map((row, i) => (
          <HistoryRow
            key={row.brand.id}
            brand={row.brand}
            date={copy.app.dates[i]}
            amount={`+${copy.money(row.amount)}`}
          />
        ))}
      </div>

      <TabBar active={0} />
    </div>
  );
};
